<script setup lang="ts">
import { ref } from "vue";

import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { CopyIcon, PlusIcon, RefreshIcon, TrashIcon } from "~/icons";
import { ButtonStyle, DialogMode, IconSize } from "~/shared/enums";

const { text, label, onRotateOrCreate } = defineProps<{
  text: string | null;
  label: string;
  rotateDialogDescription?: string;
  deleteDialogDescription?: string;
  onRotateOrCreate?: () => Promise<void>;
}>();

const emit = defineEmits<{
  delete: [];
}>();

const working = ref(false);

const copyText = () => text && copyAndNotify(label, text);

const rotateOrCreate = async () => {
  if (!onRotateOrCreate) {
    return;
  }
  working.value = true;
  await onRotateOrCreate();
  working.value = false;
  copyText();
};
</script>

<template>
  <button
    v-if="text"
    type="button"
    class="flex items-center justify-between gap-1 rounded-md border py-1 pl-3 pr-1 text-lt border-md"
    @click="copyText">
    <span class="truncate text-sm">{{ text }}</span>

    <div class="flex items-center gap-0.5">
      <Tooltip :text="`Copy ${label.toLowerCase()}`">
        <Button
          :btn-style="ButtonStyle.CHIP"
          :icon="CopyIcon"
          :icon-size="IconSize.S"
          borderless
          :a11y-label="`Copy ${label.toLowerCase()}`"
          @click.stop="copyText" />
      </Tooltip>

      <div class="flex items-center gap-0.5" @click.stop @keydown.enter.stop>
        <ConfirmationDialog
          v-if="rotateDialogDescription"
          :mode="DialogMode.DELETE"
          :title="`Rotate ${label.toLowerCase()}`"
          :description="rotateDialogDescription"
          confirm-text="Proceed"
          cancel-text="Cancel"
          :icon="RefreshIcon"
          @confirm="rotateOrCreate">
          <Tooltip :text="`Rotate ${label.toLowerCase()}`">
            <Button
              :btn-style="ButtonStyle.CHIP"
              :icon="RefreshIcon"
              :icon-size="IconSize.S"
              borderless
              :working="working"
              :a11y-label="`Rotate ${label.toLowerCase()}`" />
          </Tooltip>
        </ConfirmationDialog>

        <ConfirmationDialog
          v-if="deleteDialogDescription"
          :mode="DialogMode.DELETE"
          :title="`Delete ${label.toLowerCase()}`"
          :description="deleteDialogDescription"
          confirm-text="Proceed"
          cancel-text="Cancel"
          :icon="RefreshIcon"
          @confirm="emit('delete')">
          <Tooltip :text="`Delete ${label.toLowerCase()}`">
            <Button
              :btn-style="ButtonStyle.CHIP"
              :icon="TrashIcon"
              :icon-size="IconSize.S"
              borderless
              :a11y-label="`Delete ${label.toLowerCase()}`" />
          </Tooltip>
        </ConfirmationDialog>
      </div>
    </div>
  </button>
  <Button
    v-else
    :btn-style="ButtonStyle.SECONDARY"
    :icon="PlusIcon"
    :text="`Create ${label.toLowerCase()}`"
    :working="working"
    text-style="text-md"
    class="w-fit self-end rounded-md py-1.5 pl-2 pr-3 text-lt"
    @click="rotateOrCreate" />
</template>
